import React, { useEffect, useMemo, useState } from "react";
import {
  PayPalScriptProvider,
  PayPalButtons,
} from "@paypal/react-paypal-js";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const PayPalCheckoutButton = ({
  product,
  userId,
  onSuccess,
  onError,
}) => {
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(null);

  const isSubscription = Boolean(product?.subscription);
  const lookupKey = product?.lookup_key;

  // ---------------------------------------------------------
  // LOAD PAYPAL CONFIG
  // ---------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    const loadConfig = async () => {
      try {
        setConfigError(null);

        const response = await fetch(
          `${BACKEND}/api/paypal/config`,
          {
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(
            `PayPal config failed: HTTP ${response.status}`
          );
        }

        const data = await response.json();

        if (!data?.client_id) {
          throw new Error(
            "PayPal Client ID non presente nella configurazione"
          );
        }

        if (!cancelled) {
          setConfig(data);
        }
      } catch (error) {
        console.error(
          "PayPal configuration error:",
          error
        );

        if (!cancelled) {
          setConfigError(error);
        }
      }
    };

    loadConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------------------------------------------------------
  // PAYPAL SCRIPT OPTIONS
  // ---------------------------------------------------------

  const scriptOptions = useMemo(() => {
    if (!config?.client_id) {
      return null;
    }

    const options = {
      "client-id": config.client_id,
      currency: config.currency || "EUR",
      components: "buttons",
      "disable-funding": "card,credit,paylater",
    };

    if (isSubscription) {
      options.intent = "subscription";
      options.vault = true;
    } else {
      options.intent = "capture";
      options.vault = false;
    }

    return options;
  }, [
    config?.client_id,
    config?.currency,
    isSubscription,
  ]);

  // ---------------------------------------------------------
  // CREATE ONE-TIME ORDER
  // ---------------------------------------------------------

  const createOrder = async () => {
    if (!lookupKey) {
      throw new Error(
        "PayPal: product lookup_key mancante"
      );
    }

    if (!userId) {
      throw new Error(
        "PayPal: utente non autenticato"
      );
    }

    console.log(
      "[PayPal] Creating one-time order:",
      lookupKey
    );

    const response = await fetch(
      `${BACKEND}/api/paypal/create-order`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          lookup_key: lookupKey,
          user_id: userId,
        }),
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      console.error(
        "[PayPal] create-order failed:",
        data
      );

      throw new Error(
        data?.detail?.message ||
          data?.detail ||
          "PayPal order creation failed"
      );
    }

    if (!data?.order_id) {
      throw new Error(
        "PayPal non ha restituito un order ID"
      );
    }

    console.log(
      "[PayPal] Order created:",
      data.order_id
    );

    return data.order_id;
  };

  // ---------------------------------------------------------
  // CREATE SUBSCRIPTION
  // ---------------------------------------------------------

  const createSubscription = async () => {
    if (!lookupKey) {
      throw new Error(
        "PayPal: product lookup_key mancante"
      );
    }

    if (!userId) {
      throw new Error(
        "PayPal: utente non autenticato"
      );
    }

    console.log(
      "[PayPal] Creating subscription:",
      lookupKey
    );

    const response = await fetch(
      `${BACKEND}/api/paypal/create-subscription`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          lookup_key: lookupKey,
          user_id: userId,
        }),
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      console.error(
        "[PayPal] create-subscription failed:",
        data
      );

      throw new Error(
        data?.detail?.message ||
          data?.detail ||
          "PayPal subscription creation failed"
      );
    }

    if (!data?.subscription_id) {
      throw new Error(
        "PayPal non ha restituito un subscription ID"
      );
    }

    console.log(
      "[PayPal] Subscription created:",
      data.subscription_id
    );

    return data.subscription_id;
  };

  // ---------------------------------------------------------
  // APPROVE
  // ---------------------------------------------------------

  const handleApprove = async (data) => {
    try {
      console.log(
        "[PayPal] Approved:",
        data
      );

      // PREMIUM
      if (isSubscription) {
        if (!data?.subscriptionID) {
          throw new Error(
            "PayPal subscription ID mancante"
          );
        }

        const response = await fetch(
          `${BACKEND}/api/paypal/activate-subscription/${data.subscriptionID}`,
          {
            method: "POST",
            credentials: "include",
          }
        );

        const result = await response
          .json()
          .catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            result?.detail?.message ||
              result?.detail ||
              "Subscription activation failed"
          );
        }

        console.log(
          "[PayPal] Subscription activated:",
          result
        );
      }

      // BASIC
      else {
        if (!data?.orderID) {
          throw new Error(
            "PayPal order ID mancante"
          );
        }

        const response = await fetch(
          `${BACKEND}/api/paypal/capture-order/${data.orderID}`,
          {
            method: "POST",
            credentials: "include",
          }
        );

        const result = await response
          .json()
          .catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            result?.detail?.message ||
              result?.detail ||
              "Order capture failed"
          );
        }

        console.log(
          "[PayPal] Order captured:",
          result
        );
      }

      onSuccess?.();
    } catch (error) {
      console.error(
        "[PayPal] Approval error:",
        error
      );

      onError?.(error);
    }
  };

  // ---------------------------------------------------------
  // ERROR
  // ---------------------------------------------------------

  const handleError = (error) => {
    console.error(
      "[PayPal] SDK error:",
      error
    );

    onError?.(error);
  };

  // ---------------------------------------------------------
  // CANCEL
  // ---------------------------------------------------------

  const handleCancel = (data) => {
    console.log(
      "[PayPal] Payment cancelled:",
      data
    );
  };

  // ---------------------------------------------------------
  // LOADING
  // ---------------------------------------------------------

  if (!product) {
    return null;
  }

  if (!userId) {
    return null;
  }

  if (configError) {
    return (
      <div
        className="text-xs text-center py-3 text-muted-foreground"
        data-testid="paypal-error"
      >
        PayPal non disponibile.
      </div>
    );
  }

  if (!scriptOptions) {
    return (
      <div
        className="text-xs text-center py-3 text-muted-foreground"
        data-testid="paypal-loading"
      >
        Caricamento PayPal...
      </div>
    );
  }

  // ---------------------------------------------------------
  // PAYPAL
  // ---------------------------------------------------------

  return (
    <div
      className="paypal-wrapper w-full"
      data-testid="paypal-checkout"
    >
      <PayPalScriptProvider
        key={`${lookupKey}-${isSubscription ? "subscription" : "capture"}`}
        options={scriptOptions}
      >
        <PayPalButtons
          style={{
            layout: "horizontal",
            color: "gold",
            shape: "pill",
            label: "paypal",
            tagline: false,
            height: 45,
          }}
          createOrder={
            !isSubscription
              ? createOrder
              : undefined
          }
          createSubscription={
            isSubscription
              ? createSubscription
              : undefined
          }
          onApprove={handleApprove}
          onError={handleError}
          onCancel={handleCancel}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export default PayPalCheckoutButton;
